import type {
  CollectionReference,
  DocumentData,
  DocumentReference,
  FieldValue,
  PartialWithFieldValue,
  WriteBatch,
} from 'firebase/firestore'
import {
  collection,
  collectionGroup,
  doc,
  documentId,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { DateTime } from 'luxon'
import type { StreamInterface } from 'tricklejs/dist/types'
import {
  ObservableModelCollection,
  ObservableModelDocument,
  StaticModelCollection,
  StaticModelDocument,
  type ObservableModel,
} from '../../firestore-mobx/model'
import {
  collectionSnapshots,
  convertDocumentSnapshotToModel,
  modelItemStream,
  modelListStream,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import type { AssignmentGroupingType } from '../../models/SectionAssignment'
import { type AssignmentType } from '../../models/SectionAssignment'
import { SectionAssignment } from '../../models/SectionAssignment'
import { SectionState } from '../../types'
import { setSectionState, touchSection } from '../Section'
import type {
  draftWriteSchema,
  FirestoreSectionAssignment,
  FirestoreSectionAssignmentWrite,
} from './schema'
import { AssignmentState, empty, schema, writeSchema } from './schema'
import {
  addDocWithError,
  deleteDocWithError,
  getDocsWithError,
  getDocWithError,
  runTransactionWithError,
  updateDocWithError,
} from '../../firestore-mobx/fetch'
import { type z } from 'zod'
import { RoomState } from '../../models/RoomState'
import { partitionArray } from '../../script/seed/util'

export * from './schema'

export interface SectionAssignmentObservableModel extends ObservableModel<FirestoreSectionAssignment> {}

export interface SectionAssignmentObservableModelCollection extends ObservableModelCollection<
  SectionAssignmentObservableModel,
  FirestoreSectionAssignment
> {}

const converter: FirestoreDataConverter<FirestoreSectionAssignment> = {
  toFirestore: (data: PartialWithFieldValue<FirestoreSectionAssignment>) => {
    writeSchema.partial().parse(data)

    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getColRef = (
  firestore: Firestore,
  { sectionId }: { sectionId: string }
): CollectionReference<FirestoreSectionAssignment> => {
  return collection(
    firestore,
    'section',
    sectionId,
    'assignment'
  ).withConverter(converter)
}

const getDocRef = (
  firestore: Firestore,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
): DocumentReference<FirestoreSectionAssignment, DocumentData> => {
  return doc(
    firestore,
    'section',
    sectionId,
    'assignment',
    assignmentId
  ).withConverter(converter)
}

export const getSectionAssignments = (
  repository: FirebaseRepository,
  params: { sectionId: string; onlyPublished?: boolean }
): StreamInterface<SectionAssignment[]> => {
  const ref = getColRef(repository.firestore, params)

  if (params.onlyPublished === true)
    return modelListStream(
      repository,
      query(ref, where('assignmentState', '==', AssignmentState.active)),
      SectionAssignment
    )

  return modelListStream(repository, ref, SectionAssignment)
}

/**
 * returns the assignment with latest expiresAt date for a given section
 *
 * if no assignments with expiresAt are found for section, **returns an empty assignment**
 */
export const getAssignmentWithLatestExpiresAt = (
  repository: FirebaseRepository,
  { sectionId }: { sectionId: string }
) => {
  const ref = getColRef(repository.firestore, { sectionId })
  return collectionSnapshots(
    query(ref, orderBy('expiresAt', 'desc'), limit(1))
  ).map(({ docs }) => {
    if (!docs.length) {
      const empty = SectionAssignment.empty(repository)
      empty.changeLoadingState(false)
      return empty
    }
    const [doc] = docs
    return convertDocumentSnapshotToModel(repository, doc, SectionAssignment)
  })
}

export const getSectionHasPublishedAssignments = (
  repository: FirebaseRepository,
  { sectionId }: { sectionId: string }
) => {
  const ref = getColRef(repository.firestore, { sectionId })
  return collectionSnapshots(
    query(ref, where('assignmentState', '>', AssignmentState.draft), limit(1))
  ).map(({ docs }) => docs.length > 0)
}

export const fetchSectionAssignments = async (
  repository: FirebaseRepository,
  params: { sectionId: string; onlyPublished?: boolean }
): Promise<SectionAssignment[]> => {
  const ref = getColRef(repository.firestore, params)

  const q =
    params.onlyPublished === true
      ? query(ref, where('assignmentState', '==', AssignmentState.active))
      : query(ref)

  const snapshot = await getDocsWithError(q)
  return snapshot.docs.map((doc) =>
    convertDocumentSnapshotToModel(repository, doc, SectionAssignment)
  )
}

export const fetchSectionAssignmentsByIds = async (
  repository: FirebaseRepository,
  {
    ids,
  }: {
    ids: { sectionId: string; assignmentId: string }[]
  }
) => {
  if (!ids.length) return []

  const ref = collectionGroup(repository.firestore, 'assignment').withConverter(
    converter
  )

  const partitions = partitionArray(ids, 15)

  const snaps = await Promise.all(
    partitions.map((partition) => {
      return getDocsWithError(
        query(
          ref,
          where(
            documentId(),
            'in',
            partition.map(
              ({ sectionId, assignmentId }) =>
                `section/${sectionId}/assignment/${assignmentId}`
            )
          )
        ),
        'FetchSectionAssignmentsByIdsError'
      )
    })
  )

  return snaps
    .flatMap((snap) => snap.docs)
    .map((doc) =>
      convertDocumentSnapshotToModel(repository, doc, SectionAssignment)
    )
}

export const getSectionAssignment = (
  repository: FirebaseRepository,
  params: { sectionId: string; assignmentId: string }
): StreamInterface<SectionAssignment> => {
  const ref = getDocRef(repository.firestore, params)

  return modelItemStream(repository, ref, SectionAssignment)
}

export const fetchSectionAssignment = async (
  repository: FirebaseRepository,
  params: { sectionId: string; assignmentId: string }
) => {
  const ref = getDocRef(repository.firestore, params)
  const doc = await getDocWithError(ref, 'FetchSectionAssignmentError')
  return convertDocumentSnapshotToModel(repository, doc, SectionAssignment)
}

// deprecated

export const buildEmptySectionAssignmentModel = (
  repository: FirebaseRepository
) => {
  return new StaticModelDocument({
    repository,
    model: SectionAssignment,
    empty: empty,
  }).model
}

export const buildEmptySectionAssignmentCollection = (
  repository: FirebaseRepository
) => {
  return new StaticModelCollection({
    repository,
    model: SectionAssignment,
    empty: empty,
  })
}

export const buildSectionAssignmentObservableModelDocument = (
  repository: FirebaseRepository,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
) => {
  const ref = getDocRef(repository.firestore, {
    sectionId: sectionId,
    assignmentId: assignmentId,
  })

  return new ObservableModelDocument({
    ref,
    repository,
    model: SectionAssignment,
    empty: empty,
  })
}

export const buildSectionAssignmentObservableModelCollection = (
  repository: FirebaseRepository,
  { sectionId }: { sectionId: string }
) => {
  const ref = getColRef(repository.firestore, {
    sectionId: sectionId,
  })
  const q = query(ref)

  return new ObservableModelCollection({
    ref,
    repository,
    model: SectionAssignment,
    query: () => q,
    empty: empty,
  })
}

export const batchUpdateSectionAssignmentGroupingSize = (
  repository: FirebaseRepository,
  {
    sectionId,
    assignmentId,
    groupingSize,
    groupingSizeMaximum,
    groupingSizeMinimum,
    batch,
  }: {
    sectionId: string
    assignmentId: string
    groupingSize: number | undefined
    groupingSizeMaximum: number | undefined
    groupingSizeMinimum: number | undefined
    batch: WriteBatch
  }
) => {
  const ref = getDocRef(repository.firestore, { sectionId, assignmentId })
  batch.update(ref, {
    // maximum is a copy of groupingSize on this update path
    groupingSize,
    groupingSizeMaximum,
    groupingSizeMinimum,
    updatedAt: serverTimestamp(),
  })
}

export const updateSectionAssignment: (
  repository: FirebaseRepository,
  params: {
    sectionId: string
    assignmentId: string
    expiresAt?: DateTime | FieldValue
    assignedAt?: DateTime | FieldValue
    assignmentOverdueAt?: DateTime | FieldValue
    groupingType?: AssignmentGroupingType
    groupingSize?: number
    groupingSizeMinimum?: number
    groupingSizeMaximum?: number
    publish?: boolean
    assignmentGradingScalars?:
      | FirestoreSectionAssignment['assignmentGradingScalars']
      | FieldValue
    customGroupingSelected?: boolean
    forbidStudentGroupCreation?: boolean
    forbidStudentGroupScheduling?: boolean
    forbidStudentGroupSelection?: boolean
    forbidStudentGroupJoining?: boolean
    forbidStudentGroupLeaving?: boolean
    peerReviewExcludesSelfReview?: boolean
    peerReviewsHiddenFromReviewees?: boolean
    showFinalGradeToParticipants?: boolean
  }
) => Promise<void> = async (
  repository,
  {
    sectionId,
    assignmentId,
    expiresAt,
    assignedAt,
    assignmentOverdueAt,
    groupingSize,
    assignmentGradingScalars,
    groupingSizeMinimum,
    groupingSizeMaximum,
    groupingType,
    customGroupingSelected,
    forbidStudentGroupCreation,
    forbidStudentGroupScheduling,
    forbidStudentGroupSelection,
    forbidStudentGroupJoining,
    forbidStudentGroupLeaving,
    peerReviewExcludesSelfReview,
    peerReviewsHiddenFromReviewees,
    showFinalGradeToParticipants,
    ...rest
  }
) => {
  const ref = getDocRef(repository.firestore, { sectionId, assignmentId })

  const groupingSizeParams =
    groupingSize !== undefined
      ? {
          groupingSize: groupingSize,
          groupingSizeMaximum: groupingSizeMaximum,
          groupingSizeMinimum: groupingSizeMinimum,
        }
      : {}

  await updateDocWithError(
    ref,
    {
      ...rest,
      expiresAt:
        expiresAt instanceof DateTime ? expiresAt.toJSDate() : expiresAt,
      assignedAt:
        assignedAt instanceof DateTime ? assignedAt.toJSDate() : assignedAt,
      assignmentOverdueAt:
        assignmentOverdueAt instanceof DateTime
          ? assignmentOverdueAt.toJSDate()
          : assignmentOverdueAt,
      assignmentGradingScalars,
      updatedAt: serverTimestamp(),
      groupingType,
      ...groupingSizeParams,
      customGroupingSelected,
      forbidStudentGroupCreation,
      forbidStudentGroupScheduling,
      forbidStudentGroupSelection,
      forbidStudentGroupJoining,
      forbidStudentGroupLeaving,
      peerReviewExcludesSelfReview,
      peerReviewsHiddenFromReviewees,
      showFinalGradeToParticipants,
    },
    'UpdateSectionAssignmentExpirationAndAssignedAtError'
  )
  await touchSection(repository, sectionId)
}

export const updateSectionAssignmentGradingScalars: (
  repository: FirebaseRepository,
  params: {
    sectionId: string
    assignmentId: string
    assignmentGradingScalars?: FirestoreSectionAssignment['assignmentGradingScalars']
  }
) => Promise<void> = async (
  repository,
  { sectionId, assignmentId, assignmentGradingScalars }
) => {
  const ref = getDocRef(repository.firestore, { sectionId, assignmentId })

  await updateDocWithError(
    ref,
    {
      assignmentGradingScalars,
      updatedAt: serverTimestamp(),
    },
    'updateSectionAssignmentGroupingTypeError'
  )
  await touchSection(repository, sectionId)
}

export const updateSectionAssignmentLTIGradingLineItemId: (
  repository: FirebaseRepository,
  {
    ltiGradingLineItemId,
    sectionId,
    assignmentId,
  }: { ltiGradingLineItemId: string; sectionId: string; assignmentId: string }
) => Promise<void> = async (
  repository,
  { sectionId, assignmentId, ltiGradingLineItemId }
) => {
  const assignmentRef = getDocRef(repository.firestore, {
    sectionId,
    assignmentId,
  })

  await updateDocWithError(
    assignmentRef,
    {
      ltiGradingLineItemId,
      updatedAt: serverTimestamp(),
    },
    'UpdateSectionAssignmentLTIGradingLineItemIdError'
  )
}

export const updateSectionAssignmentState: (
  repository: FirebaseRepository,
  params: {
    sectionId: string
    assignmentId: string
    state: AssignmentState
  }
) => Promise<void> = async (repository, { sectionId, assignmentId, state }) => {
  const sectionRef = doc(repository.firestore, 'section', sectionId)
  const assignmentRef = getDocRef(repository.firestore, {
    sectionId,
    assignmentId,
  })

  await runTransactionWithError(
    repository.firestore,
    async (transaction) => {
      const doc = await transaction.get(assignmentRef)

      if (!doc.exists) {
        throw new Error('SectionAssignmentNotFound')
      }

      // update assignment state
      transaction.update(assignmentRef, {
        assignmentState: state,
        updatedAt: serverTimestamp(),
      })

      // update section (set state in progress if publishing)
      transaction.update(sectionRef, {
        sectionState:
          state === AssignmentState.active
            ? SectionState.inProgress
            : undefined,
        updatedAt: serverTimestamp(),
      })
    },
    undefined,
    'UpdateSectionAssignmentStateError'
  )
}

export const sendTestInstructorEmail: (
  repository: FirebaseRepository,
  params: { sectionId: string; assignmentId: string }
) => Promise<void> = async (repository, { sectionId, assignmentId }) => {
  const col = collection(
    repository.firestore,
    'section',
    sectionId,
    'assignment',
    assignmentId,
    'email'
  )

  // didn't bother instantiating a model for this since it's a one off
  await addDocWithError(
    col,
    {
      assignmentId: assignmentId,
      emailAddress: repository.currentUser?.email,
      sectionId: sectionId,
      startedAt: serverTimestamp(),
    },
    'SendTestInstructorEmailError'
  )
}

export async function createDraftAssignment(
  repository: FirebaseRepository,
  {
    slideDeckId,
    sectionId,
    assignmentType,
    catalogId,
    assignedAt,
    expiresAt,
    assignmentOverdueAt,
    groupingType,
    groupingSize,
    groupingSizeMinimum,
    groupingSizeMaximum,
    assignmentGradingScalars,
    customGroupingSelected,
    forbidStudentGroupCreation,
    forbidStudentGroupScheduling,
    forbidStudentGroupSelection,
    forbidStudentGroupJoining,
    forbidStudentGroupLeaving,
    peerReviewExcludesSelfReview,
    peerReviewsHiddenFromReviewees,
    showFinalGradeToParticipants,
  }: {
    slideDeckId: string
    sectionId: string
    assignmentType: AssignmentType
    catalogId?: string
    assignedAt?: DateTime
    expiresAt?: DateTime
    assignmentOverdueAt?: DateTime
    groupingType: AssignmentGroupingType
    groupingSize?: number
    groupingSizeMinimum?: number
    groupingSizeMaximum?: number
    assignmentGradingScalars?: FirestoreSectionAssignment['assignmentGradingScalars']
    customGroupingSelected?: boolean
    forbidStudentGroupCreation?: boolean
    forbidStudentGroupScheduling?: boolean
    forbidStudentGroupSelection?: boolean
    forbidStudentGroupJoining?: boolean
    forbidStudentGroupLeaving?: boolean
    peerReviewExcludesSelfReview?: boolean
    peerReviewsHiddenFromReviewees?: boolean
    showFinalGradeToParticipants?: boolean
  }
) {
  const ref = getColRef(repository.firestore, { sectionId })

  const data: z.infer<typeof draftWriteSchema> = {
    assignmentType: assignmentType,
    assignmentState: AssignmentState.draft,
    groupingType,
    slideDeckId,
    sectionId,
    updatedAt: serverTimestamp(),
    assignmentGradingScalars: assignmentGradingScalars,
    customGroupingSelected,
    forbidStudentGroupCreation,
    forbidStudentGroupScheduling,
    forbidStudentGroupSelection,
    forbidStudentGroupJoining,
    forbidStudentGroupLeaving,
    showFinalGradeToParticipants: showFinalGradeToParticipants,
  }

  if (assignedAt) data.assignedAt = assignedAt.toJSDate()
  if (expiresAt) data.expiresAt = expiresAt.toJSDate()
  if (assignmentOverdueAt)
    data.assignmentOverdueAt = assignmentOverdueAt.toJSDate()
  if (catalogId) data.catalogId = catalogId
  if (peerReviewExcludesSelfReview !== undefined)
    data.peerReviewExcludesSelfReview = peerReviewExcludesSelfReview
  if (peerReviewsHiddenFromReviewees !== undefined)
    data.peerReviewsHiddenFromReviewees = peerReviewsHiddenFromReviewees

  data.groupingSize = groupingSize ?? RoomState.maxAllowedUsers
  data.groupingSizeMaximum = groupingSizeMaximum ?? RoomState.maxAllowedUsers
  data.groupingSizeMinimum = groupingSizeMinimum ?? 1

  const docRef = await addDocWithError(ref, data, 'CreateDraftAssignmentError')

  return docRef.id
}

export async function createSectionAssignment(
  repository: FirebaseRepository,
  params: {
    sectionId: string
    assignmentType: AssignmentType
    assignedAt: DateTime
    expiresAt: DateTime
    assignmentOverdueAt?: DateTime
    groupingType: AssignmentGroupingType
    slideDeckId: string
    catalogId?: string
    groupingSize?: number
    groupingSizeMinimum?: number
    groupingSizeMaximum?: number
    assignmentGradingScalars?: FirestoreSectionAssignment['assignmentGradingScalars']
    customGroupingSelected?: boolean
    forbidStudentGroupCreation?: boolean
    forbidStudentGroupScheduling?: boolean
    forbidStudentGroupSelection?: boolean
    forbidStudentGroupJoining?: boolean
    forbidStudentGroupLeaving?: boolean
    peerReviewExcludesSelfReview?: boolean
    peerReviewsHiddenFromReviewees?: boolean
    showFinalGradeToParticipants?: boolean
  }
) {
  const ref = getColRef(repository.firestore, {
    sectionId: params.sectionId,
  })

  const data: FirestoreSectionAssignmentWrite = {
    assignedAt: params.assignedAt.toJSDate(),
    assignmentType: params.assignmentType,
    assignmentState: AssignmentState.active,
    expiresAt: params.expiresAt.toJSDate(),
    assignmentOverdueAt: params.assignmentOverdueAt?.toJSDate(),
    groupingType: params.groupingType,
    slideDeckId: params.slideDeckId,
    sectionId: params.sectionId,
    updatedAt: serverTimestamp(),
    assignmentGradingScalars: params.assignmentGradingScalars,
    customGroupingSelected: params.customGroupingSelected,
    forbidStudentGroupCreation: params.forbidStudentGroupCreation,
    forbidStudentGroupScheduling: params.forbidStudentGroupScheduling,
    forbidStudentGroupSelection: params.forbidStudentGroupSelection,
    forbidStudentGroupJoining: params.forbidStudentGroupJoining,
    forbidStudentGroupLeaving: params.forbidStudentGroupLeaving,
    showFinalGradeToParticipants: params.showFinalGradeToParticipants,
  }

  if (params.catalogId) {
    data.catalogId = params.catalogId
  }
  if (params.peerReviewExcludesSelfReview !== undefined) {
    data.peerReviewExcludesSelfReview = params.peerReviewExcludesSelfReview
  }
  if (params.peerReviewsHiddenFromReviewees !== undefined) {
    data.peerReviewsHiddenFromReviewees = params.peerReviewsHiddenFromReviewees
  }

  data.groupingSize = params.groupingSize ?? RoomState.maxAllowedUsers
  data.groupingSizeMaximum =
    params.groupingSizeMaximum ?? RoomState.maxAllowedUsers
  data.groupingSizeMinimum = params.groupingSizeMinimum ?? 1

  const docRef = await addDocWithError(
    ref,
    data,
    'CreateSectionAssignmentError'
  )

  await setSectionState(repository, {
    sectionId: params.sectionId,
    sectionState: SectionState.inProgress,
  })

  return docRef.id
}

export async function deleteSectionAssignment(
  repository: FirebaseRepository,
  params: { sectionId: string; assignmentId: string }
) {
  const ref = getDocRef(repository.firestore, params)

  await deleteDocWithError(ref, 'DeleteSectionAssignmentError')
}

export const touchAssignment = (
  repository: FirebaseRepository,
  params: {
    sectionId: string
    assignmentId: string
  }
) => {
  const payload = {
    updatedAt: serverTimestamp(),
  }
  const docRef = getDocRef(repository.firestore, params)
  return updateDocWithError(docRef, payload, 'TouchAssignmentError')
}
